import React, { useState } from 'react';
import { 
  MantineProvider, 
  AppShell, 
  Container, 
  Title, 
  Text, 
  Card, 
  Grid, 
  Group, 
  Badge, 
  Button, 
  Stack, 
  Box,
  Flex,
  Paper,
  ThemeIcon,
  rem,
  Overlay,
  createTheme,
  Avatar,
  Center
} from '@mantine/core';
import { 
  IconBrandGithub, 
  IconExternalLink, 
  IconBrandDiscord, 
  IconShield, 
  IconUsers, 
  IconDatabase, 
  IconCode,
  IconMail,
  IconArrowRight,
  IconStar,
  IconBrandLinkedin
} from '@tabler/icons-react';
import { ContactForm } from './components/ContactForm';
import { ProjectModal } from './components/ProjectModal';
import ImageModal from './components/ImageModal';
import RobloxAvatar3D from './components/RobloxAvatar3D';
import SimpleParticles from './components/SimpleParticles';
import MagneticButton from './components/MagneticButton';
import { AnimatedElement, ParallaxElement, TextReveal } from './components/ScrollAnimations';
import { AnimatedText, AnimatedBadge, AnimatedTextStyles } from './components/AnimatedText';
import { FloatingElements, OrbitalElements } from './components/FloatingElements';
import LoadingScreen from './components/LoadingScreen';
import InteractiveSkills from './components/InteractiveSkills';

// Custom theme with modern colors and gradients
const theme = createTheme({
  primaryColor: 'blue',
  colors: {
    dark: [
      '#C1C2C5',
      '#A6A7AB',
      '#909296',
      '#5c5f66',
      '#373A40',
      '#2C2E33',
      '#25262b',
      '#1A1B1E',
      '#141517',
      '#101113',
    ],
  },
  fontFamily: 'Inter, system-ui, sans-serif',
  headings: {
    fontFamily: 'Inter, system-ui, sans-serif',
    fontWeight: '700',
  },
});

function App() {
  const [contactOpened, setContactOpened] = useState(false);
  const [projectModalOpened, setProjectModalOpened] = useState(false);
  const [selectedProject, setSelectedProject] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [animationsStarted, setAnimationsStarted] = useState(false);

  const projects = [
    {
      title: 'Vaktovian Online Hub',
      description: 'Comprehensive Next.js web platform with Roblox OAuth integration and role-based access control',
      longDescription: 'A production web application built with Next.js 14 and Express.js, featuring advanced Roblox OAuth 2.0 authentication, role-based permission systems, and real-time user management. Deployed on Firebase with automated CI/CD pipelines and preview environments.',
      features: [
        'Roblox OAuth 2.0 authentication with secure session management',
        'Role-based access control with resource-level permissions',
        'Real-time user synchronization across Discord-Roblox platforms',
        'Automated CI/CD deployment with preview branches',
        'MySQL database integration with secure API endpoints',
        'Rate limiting and comprehensive security measures'
      ],
      technologies: ['Next.js 14', 'TypeScript', 'Express.js', 'MySQL', 'Firebase', 'Mantine UI', 'OAuth 2.0', 'GitHub Actions'],
      type: 'Web Application',
      icon: IconCode,
      color: 'blue',
      gradient: { from: 'red.9', to: 'dark.1' },
      images: [
        '/screenshots/oauthflow.png',
        '/screenshots/ranks.png',
        '/screenshots/breakdown.png',
        '/screenshots/profile.png'
      ],
      videos: ['/videos/vaktovia-demo.mp4'],
      liveUrl: 'https://vaktovia.org',
      githubUrl: 'https://github.com/VIA-Systems-Department/onlinehub',
      achievements: [
        'Built production-ready Next.js application with 99.9% uptime',
        'Implemented secure OAuth 2.0 flow with Roblox API integration',
        'Achieved automated deployment pipeline with preview environments',
        'Designed role-based permission system serving 1000+ users'
      ],
      challenges: [
        'Complex OAuth 2.0 integration with Roblox\'s unique API structure',
        'Implementing real-time synchronization between Discord and Roblox platforms',
        'Designing scalable role management system for large organizations'
      ]
    },
    {
      title: 'Imperial Vaktovia Bot',
      description: 'Advanced Discord.py bot with modular cog architecture for verification, progression tracking, and merit management',
      longDescription: 'A production Discord bot built with discord.py featuring a modular cog-based architecture. Integrates with Roblox APIs, MySQL databases, and external verification systems to provide comprehensive user management, training progression tracking, and merit award systems.',
      features: [
        'Discord-Roblox verification with automatic role assignment',
        'Training progression system with L1T/L2T credit tracking',
        'Merit and decoration management system',
        'Automated background tasks for progression processing',
        'Role-based permission system with rank configurations',
        'Database wrapper with progression, merit, and verification methods'
      ],
      technologies: ['Python', 'discord.py', 'MySQL', 'aiohttp', 'roblox.py', 'AsyncIO', 'Docker'],
      type: 'Discord Bot',
      icon: IconBrandDiscord,
      color: 'indigo',
      gradient: { from: 'red.4', to: 'dark.1' },
      images: [
        '/screenshots/IV1.png',
        '/screenshots/IV2.png'
      ],
      githubUrl: 'https://github.com/VIA-Systems-Department/documentation',
      achievements: [
        'Serving 1,000+ Discord users across multiple servers',
        'Processing 1,000+ commands daily with 99.9% uptime',
        'Automated verification and progression tracking with 99.9% accuracy',
        'Merit and decoration management system with 99.9% accuracy'
      ]
    },
    {
      title: 'TNIC Verification Bot',
      description: 'Advanced ticket system with algorithmic alt detection and staff management',
      longDescription: 'A sophisticated verification bot featuring machine learning-based alt detection, comprehensive ticket management system, and automated user screening processes for enhanced server security.',
      features: [
        'Ticket system to accept/deny users based on algorithmic analysis',
        'Machine learning-powered alt detection',
        'Comprehensive appeal system',
        'Assigned ticketing system with staff routing',
        'Advanced fraud detection algorithms'
      ],
      technologies: ['Discord.py', 'Machine Learning', 'Python', 'Node.js', 'MongoDB', 'TensorFlow'],
      type: 'Discord Bot',
      icon: IconShield,
      color: 'red',
      gradient: { from: 'blue.6', to: 'cyan.4' },
      images: [
        '/screenshots/verification.png',
        '/screenshots/verifcationMessage.png',
        '/screenshots/denial.png',
        '/screenshots/appealTranscript.png'
      ],

      achievements: [
        'Detected 95% of alt accounts with 99.2% accuracy',
        'Reduced manual verification workload by 85%',
        'Processed 2,000+ verification requests',
        'Maintained false positive rate under 2%'
      ],
      challenges: [
        'Training accurate ML models with limited data',
        'Balancing security with user experience',
        'Handling edge cases and false positives',
        'Adapting to evolving evasion techniques'
      ]
    },
    {
      title: 'TNIC Central Command',
      description: 'Comprehensive internal management bot for ROBLOX operations',
      longDescription: 'A powerful central command system for managing ROBLOX operations, including global ban management, credit systems, leaderboards, and automated robux payouts with comprehensive tracking and analytics.',
      features: [
        'ROBLOX Global bans management across multiple games',
        'Credits and credit leaderboards with real-time updates',
        'Comprehensive progress tracking system',
        'Automated robux payouts with audit trails',
        'Advanced analytics and reporting dashboard'
      ],
      technologies: ['Discord.py', 'Roblox API', 'Node.js', 'PostgreSQL', 'Payment APIs', 'Redis'],
      type: 'Discord Bot',
      icon: IconDatabase,
      color: 'green',
      gradient: { from: 'green.6', to: 'teal.4' },
      images: [
        '/screenshots/badgeGraph.png',
        '/screenshots/gb.png',
        '/screenshots/globalban.png',
        '/screenshots/checkap.png',
        '/screenshots/strikes.png',

      ],

      achievements: [
        'Manages bans across 15+ ROBLOX games',
        'Tracks hundreds of user credit transactions',
        'Badge system utuilzed by 100+ concurrent users'
      ],
      challenges: [
        'Integrating with ROBLOX API limitations',
        'Ensuring secure financial transactions',
        'Managing complex multi-game ban systems',
        'Handling API rate limits and downtime'
      ]
    }
  ];

  const skills = [
    {
      title: 'Full-Stack Development',
      description: 'Expert in modern web technologies including React, Next.js, Node.js, and database design.',
      icon: IconCode,
      gradient: { from: 'blue.6', to: 'cyan.4' }
    },
    {
      title: 'Discord Bot Development',
      description: 'Specialized in creating sophisticated Discord bots with advanced features and integrations.',
      icon: IconUsers,
      gradient: { from: 'indigo.6', to: 'purple.4' }
    },
    {
      title: 'OAuth & Security',
      description: 'Deep expertise in OAuth 2.0 implementations, security best practices, and authentication systems.',
      icon: IconShield,
      gradient: { from: 'violet.6', to: 'pink.4' }
    },
    {
      title: 'Database Management',
      description: 'Proficient in MySQL, database optimization, and designing scalable data architectures.',
      icon: IconDatabase,
      gradient: { from: 'green.6', to: 'teal.4' }
    }
  ];

  const handleProjectClick = (project: any) => {
    setSelectedProject(project);
    setProjectModalOpened(true);
  };

  return (
    <MantineProvider theme={theme} defaultColorScheme="dark">
      {isLoading && (
        <LoadingScreen 
          onComplete={() => {
            setIsLoading(false);
            // Start animations after loading screen fades
            setTimeout(() => setAnimationsStarted(true), 500);
          }} 
          duration={2500}
        />
      )}
      
      <SimpleParticles count={30} />
      
      <AppShell 
        header={{ height: 80 }}
        padding={0}
        style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          minHeight: '100vh'
        }}
      >
        <AppShell.Header 
          style={{
            background: 'rgba(0, 0, 0, 0.1)',
            backdropFilter: 'blur(10px)',
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
          }}
        >
          <Container size="xl" h="100%">
            <Flex justify="space-between" align="center" h="100%">
              <Group>
                <ThemeIcon size={40} radius="md" variant="gradient" gradient={{ from: 'blue', to: 'cyan' }}>
                  <img src="https://imgur.com/6mRNL6n.png" alt="scroll" style={{ width: '100%', height: '100%' }} />
                </ThemeIcon>
                <Title order={2} c="white" fw={700}>
                  scroll
                </Title>
              </Group>
              <Group>
                <Button 
                  variant="subtle" 
                  leftSection={<IconBrandGithub size={16} />}
                  c="white"
                  style={{ '&:hover': { background: 'rgba(54, 49, 49, 0.1)' } }}
                  component="a"
                  href="https://github.com/scr-oll"
                  target="_blank"
                >
                  GitHub
                </Button>
                <Button 
                  variant="subtle"
                  leftSection={<IconBrandDiscord size={16} />}
                  c="white" 
                  style={{ '&:hover': { background: 'rgba(255, 255, 255, 0.1)' } }}
                  onClick={() => setContactOpened(true)}
                >
                  Discord
                </Button>
              </Group>
            </Flex>
          </Container>
        </AppShell.Header>

        <AppShell.Main>
          {/* Hero Section with Gradient Background */}
          <Box 
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              minHeight: '100vh',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            <Container size="xl" style={{ height: '100vh' }}>
              {/* Floating Code Elements */}
              <FloatingElements count={20} type="code" />
              
              {/* Orbital Elements around hero text */}
              <OrbitalElements centerX={50} centerY={45} radius={300} count={12} />
              
              <Flex direction="column" align="center" justify="center" h="100%" style={{ position: 'relative', zIndex: 10 }}>

                
                <AnimatedText
                  variant="complexSequence"
                  delay={animationsStarted ? 1.5 : 999}
                  order={1} 
                  size={rem(72)}
                  fw={900}
                  ta="center"
                  mb="lg"
                  style={{
                    lineHeight: 1.3,
                    letterSpacing: '2px'
                  }}
                >
                  scroll
                </AnimatedText>
                
                <AnimatedElement animation="fadeInUp" delay={animationsStarted ? 7.0 : 999}>
                  <Text size="xl" mb="xl" c="rgba(255, 255, 255, 0.9)" maw={700} ta="center">
                  Specializing in Discord bots, OAuth systems, and modern web applications. 
                  I create comprehensive digital solutions that bridge multiple platforms.
                </Text>
                </AnimatedElement>
                
                <AnimatedElement animation="fadeInUp" delay={animationsStarted ? 7.5 : 999}>
                <Group justify="center" mt="xl">
                    <MagneticButton 
                    size="lg" 
                    variant="white"
                    leftSection={<IconArrowRight size={18} />}
                    onClick={() => {
                      const projectsElement = document.getElementById('projects');
                      if (projectsElement) {
                        const targetPosition = projectsElement.offsetTop;
                        const startPosition = window.pageYOffset;
                        const distance = targetPosition - startPosition;
                          const duration = 1000;
                          let startTime = 0;

                          const step = (timestamp: number) => {
                            if (!startTime) startTime = timestamp;
                            const progress = Math.min((timestamp - startTime) / duration, 1);
                            let adjustedProgress = progress;
                            const easeInOutQuart = adjustedProgress < 0.5 
                              ? 8 * adjustedProgress * adjustedProgress * adjustedProgress * adjustedProgress 
                              : 1 - 8 * (--adjustedProgress) * adjustedProgress * adjustedProgress * adjustedProgress;
                            
                            window.scrollTo(0, startPosition + distance * easeInOutQuart);
                          
                          if (progress < 1) {
                              requestAnimationFrame(step);
                          }
                        };

                          requestAnimationFrame(step);
                      }
                    }}
                    style={{
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                          boxShadow: '0 12px 40px rgba(0, 0, 0, 0.3)'
                      }
                    }}
                  >
                    View My Work
                    </MagneticButton>
                    <MagneticButton 
                    size="lg" 
                    variant="outline"
                    leftSection={<IconBrandDiscord size={18} />}
                    onClick={() => setContactOpened(true)}
                    style={{
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                      color: 'white',
                      '&:hover': {
                        background: 'rgba(255, 255, 255, 0.1)',
                        borderColor: 'rgba(255, 255, 255, 0.5)',
                        transform: 'translateY(-2px)'
                      }
                    }}
                  >
                    Get In Touch
                    </MagneticButton>
                </Group>
                </AnimatedElement>
              </Flex>
            </Container>
            
            {/* 3D Avatar Background Element - Massive Headshot */}
            <AnimatedElement animation="scaleIn" delay={animationsStarted ? 4.2 : 999}>
            <Box 
              style={{
                position: 'absolute',
                  top: '10%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '1200px',
                height: '1200px',
                  opacity: 0.4,
                  zIndex: 0,
                pointerEvents: 'none',
                  filter: 'drop-shadow(0 0 80px rgba(102, 126, 234, 0.6))'
              }}
            >
              <RobloxAvatar3D
                userId="35867965"
                width={1200}
                height={1200}
                showLoading={false}
                  autoRotate={true}
                  rotationSpeed={0.003}
                scrollRotation={true}
                scrollFrameRate={45}
                viewMode="headshot"
                style={{
                    opacity: 0.9,
                    filter: 'brightness(1.3) contrast(1.2)',
                    position: 'relative',
                    top: '-250px'
                }}
              />
            </Box>
            </AnimatedElement>

            {/* Animated background elements */}
            <ParallaxElement speed={0.2} direction="down">
            <Box 
              style={{
                position: 'absolute',
                top: '10%',
                right: '10%',
                width: '200px',
                height: '200px',
                background: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '50%',
                filter: 'blur(40px)',
                animation: 'float 6s ease-in-out infinite',
                zIndex: 0
              }}
            />
            </ParallaxElement>
            <ParallaxElement speed={0.4} direction="up">
            <Box 
              style={{
                position: 'absolute',
                bottom: '20%',
                left: '15%',
                width: '150px',
                height: '150px',
                background: 'rgba(255, 255, 255, 0.08)',
                borderRadius: '50%',
                filter: 'blur(30px)',
                animation: 'float 8s ease-in-out infinite reverse'
              }}
            />
            </ParallaxElement>
          </Box>

          {/* Projects Section with Dark Background */}
          <Box id="projects" style={{ background: '#0a0a0a' }} py={100}>
            <Container size="xl">
              <AnimatedElement animation="fadeInUp" delay={0.2}>
              <Flex direction="column" align="center" mb={80}>
                <Badge size="lg" variant="light" mb="md" color="blue">
                  Portfolio
                </Badge>
                <Title order={2} size={rem(48)} fw={700} c="white" ta="center" mb="md">
                  Featured Projects
                </Title>
                <Text size="lg" c="dimmed" ta="center" maw={600}>
                  A showcase of my most impactful work in Discord bot development, 
                  OAuth integration, and full-stack applications with real-world results.
                </Text>
              </Flex>
              </AnimatedElement>
              
              <Grid>
                {projects.map((project, index) => {
                  const IconComponent = project.icon;
                  return (
                    <Grid.Col key={index} span={{ md: 6 }}>
                      <AnimatedElement animation="fadeInUp" delay={index * 0.2}>
                      <Card 
                        shadow="xl" 
                        p="xl" 
                        radius="lg" 
                        h="100%"
                        style={{
                          background: 'linear-gradient(145deg, #1a1a1a 0%, #2a2a2a 100%)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          transition: 'all 0.3s ease',
                          cursor: 'pointer',
                          '&:hover': {
                            transform: 'translateY(-8px)',
                            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.4)',
                            border: '1px solid rgba(255, 255, 255, 0.2)'
                          }
                        }}
                        onClick={() => handleProjectClick(project)}
                      >
                        <Group justify="space-between" mb="lg">
                          <Group>
                            <ThemeIcon 
                              size={50} 
                              radius="md" 
                              variant="gradient" 
                              gradient={project.gradient}
                            >
                              <IconComponent size={28} />
                            </ThemeIcon>
                            <Title order={3} c="white" fw={600}>
                              {project.title}
                            </Title>
                          </Group>
                          <Badge 
                            variant="gradient" 
                            gradient={project.gradient}
                            size="lg"
                          >
                            {project.type}
                          </Badge>
                        </Group>

                        <Stack gap="lg">
                          <Text c="dimmed" size="md" lh={1.6}>
                            {project.description}
                          </Text>

                          <Box>
                            <Text fw={600} size="sm" mb="xs" c="white">
                              Key Features:
                            </Text>
                            <Stack gap={8}>
                              {project.features.slice(0, 3).map((feature, idx) => (
                                <Text key={idx} size="sm" c="dimmed" lh={1.5}>
                                  • {feature}
                                </Text>
                              ))}
                              {project.features.length > 3 && (
                                <Text size="sm" c="dimmed" style={{ fontStyle: 'italic' }}>
                                  +{project.features.length - 3} more features...
                                </Text>
                              )}
                            </Stack>
                          </Box>

                          <Box>
                            <Text fw={600} size="sm" mb="xs" c="white">
                              Technologies:
                            </Text>
                            <Group gap="xs">
                              {project.technologies.slice(0, 4).map((tech, idx) => (
                                <Badge 
                                  key={idx} 
                                  variant="light" 
                                  size="sm"
                                  style={{ 
                                    background: 'rgba(255, 255, 255, 0.1)',
                                    color: 'white'
                                  }}
                                >
                                  {tech}
                                </Badge>
                              ))}
                              {project.technologies.length > 4 && (
                                <Badge 
                                  variant="light" 
                                  size="sm"
                                  style={{ 
                                    background: 'rgba(255, 255, 255, 0.1)',
                                    color: 'white'
                                  }}
                                >
                                  +{project.technologies.length - 4}
                                </Badge>
                              )}
                            </Group>
                          </Box>
                        </Stack>

                        <Group mt="xl" justify="flex-end">
                            <MagneticButton 
                            variant="gradient" 
                            gradient={project.gradient}
                            leftSection={<IconExternalLink size={16} />}
                            style={{
                              '&:hover': {
                                transform: 'translateY(-2px)'
                              }
                            }}
                          >
                            View Details
                                                        </MagneticButton>
                        </Group>
                      </Card>
                      </AnimatedElement>
                    </Grid.Col>
                  );
                })}
              </Grid>
            </Container>
          </Box>

          {/* Enhanced Skills Section */}
          <Box 
            style={{
              background: 'linear-gradient(135deg, #2D1B69 0%, #11998e 100%)',
            }} 
            py={100}
          >
            <Container size="xl">
              <InteractiveSkills />
            </Container>
          </Box>

          {/* Groups Served Section */}
          <Box 
            style={{
              background: 'linear-gradient(135deg, #1A0B2E 0%, #16213E 50%, #0F3460 100%)',
              position: 'relative',
              overflow: 'hidden'
            }} 
            py={100}
          >
            <Container size="xl">
              <AnimatedElement animation="fadeInUp" delay={0.2}>
                <Title 
                  order={2} 
                  size={rem(48)} 
                  fw={900} 
                  ta="center" 
                  mb={rem(20)}
                  style={{
                    background: 'linear-gradient(45deg, #667eea 0%, #764ba2 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    color: 'transparent',
                    letterSpacing: '1px'
                  }}
                >
                  Clans Served
                </Title>
              </AnimatedElement>

              <AnimatedElement animation="fadeInUp" delay={0.4}>
                <Text 
                  size="xl" 
                  ta="center" 
                  mb={rem(60)} 
                  c="rgba(255, 255, 255, 0.8)"
                  maw={800}
                  mx="auto"
                >
                  Trusted by top clans to deliver exceptional integration solutions
                </Text>
              </AnimatedElement>

              <Grid gutter="xl">
                <Grid.Col span={{ base: 12, md: 6, lg: 4 }}>
                  <AnimatedElement animation="scaleIn" delay={0.6}>
                      <Card
                        style={{
                          background: 'rgba(255, 255, 255, 0.1)',
                          backdropFilter: 'blur(10px)',
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                          borderRadius: '16px',
                          height: '100%'
                        }}
                        p="xl" 
                      >
                        <Center mb="lg">
                          <Avatar 
                            src="/VAK_triad.png"
                            size={80} 
                            radius="md"
                            
                          />
                        </Center>
                        <Text ta="center" size="xl" fw={700} c="white" mb="sm">
                          The Vaktovian Empire
                        </Text>
                        <Text ta="center" c="rgba(255, 255, 255, 0.7)" mb="md">
                          The Best Powerclan on the Game
                        </Text>
                        <Text ta="center" size="sm" c="rgba(255, 255, 255, 0.6)">
                          Systems Department Manager. Created the Online Hub, verification flow, numerous API endpoints, and many more integrations.
                        </Text>
                        <Group justify="center" mt="lg" gap="xs">
                          <Badge variant="light" color="white" size="sm">Power Clan</Badge>
                          <Badge variant="light" color="white" size="sm">Project Management</Badge>
                        </Group>
                      </Card>
                    </AnimatedElement>
                  </Grid.Col>

                  <Grid.Col span={{ base: 12, md: 6, lg: 4 }}>
                    <AnimatedElement animation="scaleIn" delay={0.8}>
                      <Card
                        style={{
                          background: 'rgba(255, 255, 255, 0.1)',
                          backdropFilter: 'blur(10px)',
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                          borderRadius: '16px',
                          height: '100%'
                        }}
                        p="xl"
                      >
                        <Center mb="lg">
                          <Avatar 
                            src="/tnic.png"
                            size={80} 
                            radius="md" 
                          />
                        </Center>
                        <Text ta="center" size="xl" fw={700} c="white" mb="sm">
                          TNI: Commandos
                        </Text>
                        <Text ta="center" c="rgba(255, 255, 255, 0.7)" mb="md">
                          TNI's exclusive gunfighting division
                        </Text>
                        <Text ta="center" size="sm" c="rgba(255, 255, 255, 0.6)">
                          Automation engineering lead. Leading & creating numerous automation systems within discord, API endpoints, Google AppsScript, and more.
                        </Text>
                        <Group justify="center" mt="lg" gap="xs">
                          <Badge variant="light" color="white" size="sm">Automation</Badge>
                          <Badge variant="light" color="white" size="sm">AppsScript</Badge>
                          <Badge variant="light" color="white" size="sm">Discord Automation</Badge>
                          <Badge variant="light" color="white" size="sm">Member Intake Management</Badge>
                        </Group>
                      </Card>
                    </AnimatedElement>
                  </Grid.Col>

                  <Grid.Col span={{ base: 12, md: 6, lg: 4 }}>
                    <AnimatedElement animation="scaleIn" delay={1.0}>
                      <Card
                        style={{
                          background: 'rgba(255, 255, 255, 0.1)',
                          backdropFilter: 'blur(10px)',
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                          borderRadius: '16px',
                          height: '100%'
                        }}
                        p="xl"
                      >
                        <Center mb="lg">
                          <Avatar 
                            src="/tni.png"
                            size={80} 
                            radius="md"
                          />
                        </Center>
                        <Text ta="center" size="xl" fw={700} c="white" mb="sm">
                          The Nighthawk Imperium
                        </Text>
                        <Text ta="center" c="rgba(255, 255, 255, 0.7)" mb="md">
                          One of the Largest Clans in the Genre
                        </Text>
                        <Text ta="center" size="sm" c="rgba(255, 255, 255, 0.6)">
                          Innovation research, automated payouts, and asset security.
                        </Text>
                        <Group justify="center" mt="lg" gap="xs">
                          <Badge variant="light" color="white" size="sm">Asset Security</Badge>
                          <Badge variant="light" color="white" size="sm">Innovation</Badge>
                        </Group>
                      </Card>
                    </AnimatedElement>
                  </Grid.Col>
                </Grid>

              <AnimatedElement animation="fadeInUp" delay={1.2}>
                <Box ta="center" mt={60}>
                  <Text size="lg" c="rgba(255, 255, 255, 0.9)" mb="lg">
                    Ready to enhance your organization's operations?
                  </Text>
                  <MagneticButton 
                    size="lg" 
                            variant="gradient" 
                    gradient={{ from: 'blue.6', to: 'cyan.4' }}
                    leftSection={<IconMail size={18} />}
                    onClick={() => setContactOpened(true)}
                    style={{
                      boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
                    }}
                  >
                    Get Started Today
                  </MagneticButton>
                </Box>
              </AnimatedElement>
            </Container>

            {/* Background decoration */}
            <Box
              style={{
                position: 'absolute',
                top: '20%',
                left: '-10%',
                width: '300px',
                height: '300px',
                background: 'radial-gradient(circle, rgba(102, 126, 234, 0.1) 0%, transparent 70%)',
                borderRadius: '50%',
                filter: 'blur(40px)',
                zIndex: 0
              }}
            />
            <Box
              style={{
                position: 'absolute',
                bottom: '10%',
                right: '-5%',
                width: '400px',
                height: '400px',
                background: 'radial-gradient(circle, rgba(118, 75, 162, 0.1) 0%, transparent 70%)',
                borderRadius: '50%',
                filter: 'blur(60px)',
                zIndex: 0
              }}
            />
          </Box>

          {/* Testimonials Section */}
          <Box 
            style={{
              background: 'linear-gradient(135deg, #0F0C29 0%, #24243e 50%, #302b63 100%)',
              position: 'relative',
              overflow: 'hidden'
            }} 
            py={100}
          >
            <Container size="xl">
              <AnimatedElement animation="fadeInUp" delay={0.2}>
                <Title 
                  order={2} 
                  size={rem(48)} 
                  fw={900} 
                  ta="center" 
                  mb={rem(20)}
                  style={{
                    background: 'linear-gradient(45deg, #667eea 0%, #764ba2 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    color: 'transparent',
                    letterSpacing: '1px'
                  }}
                >
                  What People Have to Say
                          </Title>
              </AnimatedElement>

              <AnimatedElement animation="fadeInUp" delay={0.4}>
                <Text 
                  size="xl" 
                  ta="center" 
                  mb={rem(60)} 
                  c="rgba(255, 255, 255, 0.8)"
                  maw={800}
                  mx="auto"
                >
                  Hear from the people who've experienced my solutions firsthand
                        </Text>
              </AnimatedElement>

                            <Stack gap="xl">
                <AnimatedElement animation="fadeInLeft" delay={0.6}>
                  <Card
                    style={{
                      background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)',
                      backdropFilter: 'blur(20px)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '24px',
                      overflow: 'hidden',
                      position: 'relative'
                    }}
                    p={0}
                  >
                                                             <Flex direction={{ base: 'column', md: 'row' }} align="stretch">
                      <Box
                        style={{
                          position: 'relative',
                          minHeight: '200px',
                          background: 'linear-gradient(45deg, #d23110, #764ba2)',
                          display: 'flex',
                          alignItems: 'flex-end',
                          justifyContent: 'center',
                          width: '100%',
                          flexShrink: 0
                        }}
                        className="testimonial-avatar-box"
                      >
                         <RobloxAvatar3D
                           userId="128210489"
                           width={180}
                           height={200}
                           showLoading={false}
                           autoRotate={false}
                           viewMode="headshot"
                           style={{
                             filter: 'brightness(1.2) contrast(1.1) drop-shadow(0 4px 12px rgba(0,0,0,0.3))'
                           }}
                         />
                         {/* Floating particles around avatar */}
                         <Box
                           style={{
                             position: 'absolute',
                             top: '20%',
                             right: '10%',
                             width: '8px',
                             height: '8px',
                             background: 'rgba(255, 255, 255, 0.8)',
                             borderRadius: '50%',
                             animation: 'float 3s ease-in-out infinite'
                           }}
                         />
                         <Box
                           style={{
                             position: 'absolute',
                             bottom: '25%',
                             left: '15%',
                             width: '6px',
                             height: '6px',
                             background: 'rgba(255, 255, 255, 0.6)',
                             borderRadius: '50%',
                             animation: 'float 4s ease-in-out infinite reverse'
                           }}
                         />
                       </Box>
                       <Box p="xl" style={{ position: 'relative', flexGrow: 1 }}>
                         <Text 
                           size="xl" 
                           c="white" 
                           mb="lg" 
                           style={{ 
                             fontStyle: 'italic', 
                             lineHeight: 1.6,
                             fontSize: '1.25rem',
                             fontWeight: 300
                           }}
                         >
                           "...his exceptional efforts as the sole technical lead on this project, a role that requires not only skill but immense dedication. His creative spirit and intent on improving Vaktovian technology have truly made him an architect of progress."
                         </Text>
                         <Group justify="space-between" align="center">
                           <Box>
                             <Text fw={700} c="white" size="lg">
                               Aurevus
                             </Text>
                             <Text c="rgba(255, 255, 255, 0.7)" size="sm">
                               Vaktovian Systems Department Head
                             </Text>
                           </Box>
                           <Box
                             style={{
                               display: 'flex',
                               gap: '4px'
                             }}
                           >
                             {[...Array(5)].map((_, i) => (
                               <Text 
                                 key={i}
                                 style={{
                                   color: '#FFD700',
                                   fontSize: '1.2rem'
                                 }}
                               >
                                 ★
                               </Text>
                             ))}
                           </Box>
                         </Group>
                       </Box>
                     </Flex>
                  </Card>
                </AnimatedElement>

                <AnimatedElement animation="fadeInRight" delay={0.8}>
                  <Card
                    style={{
                      background: 'linear-gradient(135deg, rgba(255, 107, 107, 0.15) 0%, rgba(255, 142, 83, 0.15) 100%)',
                      backdropFilter: 'blur(20px)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '24px',
                      overflow: 'hidden',
                      position: 'relative'
                    }}
                    p={0}
                  >
                    <Flex direction={{ base: 'column', md: 'row-reverse' }} align="stretch">
                      <Box
                        style={{
                          position: 'relative',
                          minHeight: '200px',
                          background: 'linear-gradient(45deg, #6f3989, #31193c)',
                          display: 'flex',
                          alignItems: 'flex-end',
                          justifyContent: 'center',
                          width: '100%',
                          flexShrink: 0
                        }}
                        className="testimonial-avatar-box"
                      >
                        <RobloxAvatar3D
                          userId="742322"
                          width={180}
                          height={200}
                          showLoading={false}
                          autoRotate={false}
                          viewMode="headshot"
                          style={{
                            filter: 'brightness(1.2) contrast(1.1) drop-shadow(0 4px 12px rgba(0,0,0,0.3))'
                          }}
                        />
                        {/* Floating particles around avatar */}
                        <Box
                          style={{
                            position: 'absolute',
                            top: '15%',
                            left: '10%',
                            width: '10px',
                            height: '10px',
                            background: 'rgba(255, 255, 255, 0.9)',
                            borderRadius: '50%',
                            animation: 'float 2.5s ease-in-out infinite'
                          }}
                        />
                        <Box
                          style={{
                            position: 'absolute',
                            bottom: '25%',
                            right: '15%',
                            width: '6px',
                            height: '6px',
                            background: 'rgba(255, 255, 255, 0.7)',
                            borderRadius: '50%',
                            animation: 'float 4s ease-in-out infinite reverse'
                          }}
                        />
                      </Box>
                      <Box p="xl" style={{ position: 'relative', flex: 1 }}>
                        <Text 
                          size="xl" 
                          c="white" 
                          mb="lg" 
                          style={{ 
                            fontStyle: 'italic', 
                            lineHeight: 1.6,
                            fontSize: '1.25rem',
                            fontWeight: 300
                          }}
                        >
                          "I have just taken a look at the website you have created and I am completely blown away, it is nothing short of magnificent! Not only is it stylishly designed, it is also incredibly informational and very easy to navigate. "
                        </Text>
                        <Group justify="space-between" align="center">
                          <Box>
                            <Text fw={700} c="white" size="lg">
                              Emperor Vaktus
                            </Text>
                            <Text c="rgba(255, 255, 255, 0.7)" size="sm">
                              Emperor of Vaktovia
                            </Text>
                          </Box>
                          <Box
                            style={{
                              display: 'flex',
                              gap: '4px'
                            }}
                          >
                            {[...Array(5)].map((_, i) => (
                              <Text 
                                key={i}
                                style={{
                                  color: '#FFD700',
                                  fontSize: '1.2rem'
                                }}
                              >
                                ★
                              </Text>
                            ))}
                          </Box>
                        </Group>
                      </Box>
                    </Flex>
                  </Card>
                </AnimatedElement>

                <AnimatedElement animation="fadeInLeft" delay={1.0}>
                  <Card
                    style={{
                      background: 'linear-gradient(135deg, rgba(78, 205, 196, 0.15) 0%, rgba(68, 160, 141, 0.15) 100%)',
                      backdropFilter: 'blur(20px)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '24px',
                      overflow: 'hidden',
                      position: 'relative'
                    }}
                    p={0}
                  >
                    <Flex direction={{ base: 'column', md: 'row' }} align="stretch">
                      <Box
                        style={{
                          position: 'relative',
                          minHeight: '200px',
                          background: 'linear-gradient(45deg, #1bbabb,rgb(4, 92, 92))',
                          display: 'flex',
                          alignItems: 'flex-end',
                          justifyContent: 'center',
                          width: '100%',
                          flexShrink: 0
                        }}
                        className="testimonial-avatar-box"
                      >
                        <RobloxAvatar3D
                          userId="170908479"
                          width={180}
                          height={200}
                          showLoading={false}
                          autoRotate={false}
                          viewMode="headshot"
                          style={{
                            filter: 'brightness(1.2) contrast(1.1) drop-shadow(0 4px 12px rgba(0,0,0,0.3))'
                          }}
                        />
                        {/* Floating particles around avatar */}
                        <Box
                          style={{
                            position: 'absolute',
                            bottom: '20%',
                            right: '15%',
                            width: '7px',
                            height: '7px',
                            background: 'rgba(255, 255, 255, 0.7)',
                            borderRadius: '50%',
                            animation: 'float 3.5s ease-in-out infinite reverse'
                          }}
                        />
                        <Box
                          style={{
                            position: 'absolute',
                            top: '25%',
                            left: '10%',
                            width: '9px',
                            height: '9px',
                            background: 'rgba(255, 255, 255, 0.8)',
                            borderRadius: '50%',
                            animation: 'float 2.8s ease-in-out infinite'
                          }}
                        />
                      </Box>
                      <Box p="xl" style={{ position: 'relative', flex: 1 }}>
                        <Text 
                          size="xl" 
                          c="white" 
                          mb="lg" 
                          style={{ 
                            fontStyle: 'italic', 
                            lineHeight: 1.6,
                            fontSize: '1.25rem',
                            fontWeight: 300
                          }}
                        >
                          "Innovative solutions that actually work. The verifcation system has been a game changer for welfare and TNI:C as a whole."
                        </Text>
                        <Group justify="space-between" align="center">
                          <Box>
                            <Text fw={700} c="white" size="lg">
                              gaine_d
                            </Text>
                            <Text c="rgba(255, 255, 255, 0.7)" size="sm">
                              Pregression Deputy
                            </Text>
                          </Box>
                          <Box
                            style={{
                              display: 'flex',
                              gap: '4px'
                            }}
                          >
                            {[...Array(5)].map((_, i) => (
                              <Text 
                                key={i}
                                style={{
                                  color: '#FFD700',
                                  fontSize: '1.2rem'
                                }}
                              >
                                ★
                              </Text>
                            ))}
                          </Box>
                        </Group>
                      </Box>
                    </Flex>
                  </Card>
                </AnimatedElement>
              </Stack>
            </Container>

            {/* Background decoration */}
            <Box
              style={{
                position: 'absolute',
                top: '30%',
                right: '-10%',
                width: '250px',
                height: '250px',
                background: 'radial-gradient(circle, rgba(102, 126, 234, 0.1) 0%, transparent 70%)',
                borderRadius: '50%',
                filter: 'blur(30px)',
                zIndex: 0
              }}
            />
            <Box
              style={{
                position: 'absolute',
                bottom: '20%',
                left: '-8%',
                width: '300px',
                height: '300px',
                background: 'radial-gradient(circle, rgba(255, 107, 107, 0.1) 0%, transparent 70%)',
                borderRadius: '50%',
                filter: 'blur(40px)',
                zIndex: 0
              }}
            />
          </Box>
        </AppShell.Main>
      </AppShell>

      <ContactForm opened={contactOpened} onClose={() => setContactOpened(false)} />
      <ProjectModal 
        opened={projectModalOpened} 
        onClose={() => setProjectModalOpened(false)} 
        project={selectedProject}
      />
      
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(5deg); }
        }
        
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
        
        * {
          transition: all 0.3s ease;
        }
        
        /* Enhanced magnetic button effects */
        .mantine-Button-root {
          cursor: pointer !important;
          will-change: transform;
        }
        
        .mantine-Button-root:hover {
          transform: translateY(-2px) !important;
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15) !important;
        }
        
        /* Testimonial avatar responsive width */
        @media (min-width: 768px) {
          .testimonial-avatar-box {
            width: 300px !important;
          }
        }
        
        /* Ensure z-index hierarchy */
        .mantine-AppShell-main {
          position: relative;
          z-index: 10;
        }
        
        .mantine-AppShell-header {
          position: relative;
          z-index: 20;
        }
        
        .mantine-Container-root {
          position: relative;
          z-index: 15;
        }
      `}</style>
      <AnimatedTextStyles />
    </MantineProvider>
  );
}

export default App;
